import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { IFraudTrendEvidence, IListCheckEntry } from 'src/ts/interfaces/listCheck';

type FraudTrendEvidenceStatus = 'idle' | 'loading' | 'success' | 'missing' | 'error';

export interface FraudTrendEvidenceResult {
  status: FraudTrendEvidenceStatus;
  data: IFraudTrendEvidence | null;
}

export interface FraudTrendEvidenceState {
  evidenceFor: (checkKey: string) => FraudTrendEvidenceResult;
}

const IDLE: FraudTrendEvidenceResult = { status: 'idle', data: null };
const LOADING: FraudTrendEvidenceResult = { status: 'loading', data: null };
const FAILED: FraudTrendEvidenceResult = { status: 'error', data: null };
const MISSING: FraudTrendEvidenceResult = { status: 'missing', data: null };

const keyOf = (proofId: string, checkKey: string) => `${proofId}|${checkKey}`;

export const useFraudTrendEvidence = (
  proofId: string | undefined,
  rulings: IListCheckEntry[] | null
): FraudTrendEvidenceState => {
  const requestedRef = useRef<Set<string>>(new Set());
  const cancelSourceRef = useRef(axios.CancelToken.source());
  const [results, setResults] = useState<Record<string, FraudTrendEvidenceResult>>({});

  useEffect(() => {
    const cancelSource = cancelSourceRef.current;
    return () => {
      cancelSource.cancel();
    };
  }, []);

  useEffect(() => {
    if (!proofId || !rulings) return;
    const requested = requestedRef.current;

    rulings
      .filter((entry) => entry.is_fraud_trend && entry.has_evidence)
      .map((entry) => entry.check_key)
      .filter((checkKey) => !requested.has(keyOf(proofId, checkKey)))
      .forEach((checkKey) => {
        const key = keyOf(proofId, checkKey);
        requested.add(key);
        setResults((prev) => ({ ...prev, [key]: LOADING }));

        const fetchEvidence = async () => {
          let outcome: FraudTrendEvidenceResult;
          try {
            const body = (await defaultInstance.get(
              `${
                configVars.server_url
              }/v2/proofs/${proofId}/list_check_rulings/${encodeURIComponent(checkKey)}/evidence`,
              { cancelToken: cancelSourceRef.current.token }
            )) as unknown as IFraudTrendEvidence;
            outcome = { status: 'success', data: body };
          } catch (error) {
            if (axios.isCancel(error)) return;
            if (axios.isAxiosError(error) && error.response?.status === 404) {
              outcome = MISSING;
            } else {
              requested.delete(key);
              outcome = FAILED;
            }
          }
          setResults((prev) => ({ ...prev, [key]: outcome }));
        };

        fetchEvidence();
      });
  }, [proofId, rulings]);

  const evidenceFor = useCallback(
    (checkKey: string) => (proofId && results[keyOf(proofId, checkKey)]) || IDLE,
    [proofId, results]
  );

  return { evidenceFor };
};
